import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import DataSourcesService from '../service';
import { useAsync } from '../../utils/customHooks';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import css from './SchemaColumnsShelf.css';

const DataSourcesAPI = new DataSourcesService();
const CSV_HEADERS = ['Field Name', 'Field Type', 'Dimension / Metric'];

const escapeCsvValue = value => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

const downloadCsv = (filename, columns) => {
    const rows = columns.map(({ name, type, is_metric: isMetric }) =>
        [name, type, isMetric ? 'Metric' : 'Dimension'].map(escapeCsvValue).join(',')
    );
    const blob = new Blob([[CSV_HEADERS.join(','), ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

const SchemaColumnsShelf = ({ uanId, translate }) => {
    const [state, run] = useAsync();

    useEffect(() => {
        run(DataSourcesAPI.getSchemaColumns(uanId));
    }, [uanId, run]);

    const columns = useMemo(() => state.data?.columns || [], [state.data]);
    const failed = Boolean(state.error) || (state.data && !state.data.columns);

    return (
        <div className={css.container}>
            <div className={css.schemaName}>{translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.SUBTITLE')}</div>
            {failed && (
                <div className={css.message}>
                    {translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.LOAD_ERROR')}
                </div>
            )}
            {state.data?.columns && (
                <>
                    <div className={css.toolbar}>
                        <span>
                            {translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.FIELDS_COUNT', {
                                count: columns.length,
                            })}
                        </span>
                        <button
                            type="button"
                            className={css.downloadButton}
                            onClick={() => downloadCsv(state.data.filename, columns)}
                        >
                            <Icon name="file-type-csv" size={IconSizes.SMALL} />
                            {translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.DOWNLOAD_CSV')}
                        </button>
                    </div>
                    <div className={css.table}>
                        <div className={`${css.row} ${css.headerRow}`}>
                            <div className={css.nameCell}>
                                {translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.FIELD_NAME')}
                            </div>
                            <div className={css.typeCell}>
                                {translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.TYPE')}
                            </div>
                            <div className={css.kindCell}>
                                {translate('STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.KIND')}
                            </div>
                        </div>
                        {columns.map(({ name, type, is_metric: isMetric }) => (
                            <div key={name} className={css.row}>
                                <div className={`${css.nameCell} ${css.fieldName}`}>{name}</div>
                                <div className={css.typeCell}>{type}</div>
                                <div className={css.kindCell}>
                                    {translate(
                                        `STATIC.PAGES.DATA_DESTINATIONS.SCHEMA_COLUMNS.${
                                            isMetric ? 'METRIC' : 'DIMENSION'
                                        }`
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

SchemaColumnsShelf.propTypes = {
    uanId: PropTypes.number.isRequired,
    translate: PropTypes.func.isRequired,
};

export default SchemaColumnsShelf;
